import { ETaskStatus } from '@constants';
import { ITask } from '@sdk/contracts';
import { dateProvider } from '@utils';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Popconfirm,
  Tooltip
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreatedByWrapper, MandatoryWrapper, TitleWrapper, Wrapper } from './Task.styles';

import { TeacherCourseSelector } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { TaskStatusBadge } from '@/components/TaskStatusBadge';
import { RULES } from '@/constants';
import { useMutateActivity, useProfile, useActivityForm } from '@/store';

type Props = {
  onChange?: (task?: ITask) => void;
  courseId: string;
};

export const Task: React.FC<Props> = ({ onChange, courseId }) => {
  const { t } = useTranslation('CourseTranslations');
  const { data, setData, course } = useActivityForm();
  const {
    roleCheck: { isManager }
  } = useProfile();
  const { isWorking, removeTask } = useMutateActivity();
  const [isOpen, setIsOpen] = useState(false);
  const [isSameAsActivity, setIsSameAsActivity] = useState(false);
  const task = data?.task;
  const activityId = data?._id;

  const isDisabled = useMemo(() => task?.statusInfo.currentStatus === ETaskStatus.APPROVED, [task]);
  const title = useMemo(() => (task ? t('ACTIVITY_TASK') : t('ACTIVITY_CREATE_TASK')), [task]);
  const canRemoveTask = useMemo(
    () => activityId && task && task.statusInfo.currentStatus !== ETaskStatus.APPROVED,
    [activityId, task]
  );

  useEffect(() => {
    if (!task) return;
    setIsOpen(true);
  }, [task]);

  const onClick = () => {
    if (isOpen) return;
    setIsOpen(true);
  };

  const onCancel = () => {
    onChange?.();
    setIsOpen(false);
    setData({ ...data, task: undefined });
  };

  const onRemoveTask = () => {
    if (!activityId || !task?._id) return;
    removeTask({ activityId, taskId: task._id }, { onSuccess: onCancel });
  };

  if (!isManager || !course) return null;

  return (
    <Wrapper $isPointer={!isOpen} onClick={onClick}>
      <Flex justify="space-between" style={{ width: '100%' }} align="center">
        <Flex gap={8} align="center">
          <Icon i="TaskAdd" size="16" />
          <TitleWrapper>{title}</TitleWrapper>
        </Flex>

        <Flex align="center">
          {task && (
            <CreatedByWrapper>
              {t('ACTIVITY_TASK_CREATED_BY', {
                date: dayjs(task.createdAt).format('DD/MM/YYYY'),
                name: `${task.creator.name} ${task.creator.lastName.charAt(0)}.`
              })}
            </CreatedByWrapper>
          )}
        </Flex>
      </Flex>

      {isOpen && (
        <>
          <Form.Item name={['task', '_id']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={['task', 'course']} hidden initialValue={courseId}>
            <Input />
          </Form.Item>
          {!isSameAsActivity && (
            <>
              <Form.Item name={['task', 'title']} rules={[RULES.REQUIRED]}>
                <Input placeholder={t('ACTIVITY_TASK_TITLE')} disabled={isDisabled} />
              </Form.Item>
              <Form.Item name={['task', 'description']} rules={[RULES.REQUIRED]}>
                <Input.TextArea
                  placeholder={t('ACTIVITY_TASK_DESCRIPTION')}
                  rows={5}
                  disabled={isDisabled}
                />
              </Form.Item>
            </>
          )}
          {!task && (
            <Checkbox
              value={isSameAsActivity}
              onChange={e => setIsSameAsActivity(e.target.checked)}
              disabled={isDisabled}>
              {t('ACTIVITY_TASK_SIMILAR_TO_ACTIVITY')}
            </Checkbox>
          )}
          <Divider />
          <Form.Item name={['task', 'assignee']} rules={[RULES.REQUIRED]}>
            <TeacherCourseSelector teachers={course.teachers} />
          </Form.Item>
          <Form.Item name={['task', 'deadline']} rules={[RULES.REQUIRED]}>
            <DatePicker
              format="DD/MM/YYYY"
              disabledDate={date => dateProvider.isBeforeTomorrow(date.toDate())}
              disabled={isDisabled}
            />
          </Form.Item>
          {!task && <MandatoryWrapper>{t('ACTIVITY_TASK_MANDATORY_INFO')}</MandatoryWrapper>}
          {task && (
            <Flex gap={8}>
              <TaskStatusBadge status={task.statusInfo.currentStatus} />
              <Tooltip title={t('ACTIVITY_TASK_STATUS_INFO')} placement="top">
                <Button size="small" type="link" icon={<Icon i="Information" />} />
              </Tooltip>
            </Flex>
          )}
          {canRemoveTask && (
            <Popconfirm
              placement="top"
              onConfirm={onRemoveTask}
              title={t('ARE_YOU_SURE_TO_DELETE_TASK')}>
              <Button
                danger
                size="small"
                type="link"
                loading={isWorking}
                icon={<Icon i="TrashCan" />}>
                {t('ACTIVITY_REMOVE_TASK')}
              </Button>
            </Popconfirm>
          )}
          {!task && (
            <Flex justify="end" style={{ width: '100%' }}>
              <Button type="default" onClick={onCancel}>
                {t('CANCEL')}
              </Button>
            </Flex>
          )}
        </>
      )}
    </Wrapper>
  );
};
